//src/pages/cadastros/GroupsPage.tsx
import { useEffect, useMemo, useState } from "react";
import {
  Shield,
  Search,
  RefreshCw,
  KeyRound,
  Plus,
  Users,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { PERMISSIONS } from "@/lib/permissions";
import { PermissionButton } from "@/components/shared/PermissionButton";
import { toast } from "sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { PageHeader } from "@/components/shared/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CrudCardActions } from "@/components/shared/CrudCardActions";
import {
  useCreateGroup,
  useDeleteGroup,
  useGroupDetail,
  useGroups,
  usePatchGroupStatus,
  usePermissionsCatalog,
  useUpdateGroup,
  useUpdateGroupPermissions,
} from "@/hooks/useGroups";
import type {
  GroupListItem,
  GroupUpsertIn,
  PermissionListItem,
} from "@/services/groups";

function normalizeText(v: string) {
  return (v || "").toLowerCase().trim();
}

function getGroupTone(name: string) {
  const value = normalizeText(name);

  if (value === "admin") return "border-red-200 bg-red-50 text-red-700";
  if (value === "gestor") return "border-blue-200 bg-blue-50 text-blue-700";
  if (value === "operador") return "border-amber-200 bg-amber-50 text-amber-700";
  if (value === "consulta") return "border-emerald-200 bg-emerald-50 text-emerald-700";

  return "border-muted bg-muted/40 text-muted-foreground";
}

type GroupFormState = {
  name: string;
  description: string;
  is_active: boolean;
};

const EMPTY_FORM: GroupFormState = {
  name: "",
  description: "",
  is_active: true,
};

function groupToForm(group?: GroupListItem | null): GroupFormState {
  if (!group) return EMPTY_FORM;
  return {
    name: group.name || "",
    description: group.description || "",
    is_active: !!group.is_active,
  };
}

function groupToPayload(form: GroupFormState): GroupUpsertIn {
  return {
    name: form.name.trim(),
    description: form.description.trim() || null,
    is_active: form.is_active,
  };
}

export default function GroupsPage() {
  const { user } = useAuth();

  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(true);

  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<GroupListItem | null>(null);
  const [groupForm, setGroupForm] = useState<GroupFormState>(EMPTY_FORM);

  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [permissionsTarget, setPermissionsTarget] = useState<GroupListItem | null>(null);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<string[]>([]);
  const [permissionSearch, setPermissionSearch] = useState("");

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<GroupListItem | null>(null);

  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusTarget, setStatusTarget] = useState<GroupListItem | null>(null);

  const userPermissions = user?.permissions ?? [];
  const canReadGroups = userPermissions.includes(PERMISSIONS.GROUPS_READ);
  const canWriteGroups = userPermissions.includes(PERMISSIONS.GROUPS_WRITE);
  const canReadPermissionsCatalog = userPermissions.includes(PERMISSIONS.PERMISSIONS_READ);
  const canManageGroupPermissions = canWriteGroups && canReadPermissionsCatalog;

  const groupsQ = useGroups(includeInactive);
  const permissionsQ = usePermissionsCatalog(canManageGroupPermissions);
  const groupDetailQ = useGroupDetail(
    canManageGroupPermissions && permissionsDialogOpen ? permissionsTarget?.id : undefined
  );

  const createMut = useCreateGroup();
  const updateMut = useUpdateGroup();
  const patchStatusMut = usePatchGroupStatus();
  const deleteMut = useDeleteGroup();
  const updatePermsMut = useUpdateGroupPermissions();

  const isBusy =
    groupsQ.isFetching ||
    createMut.isPending ||
    updateMut.isPending ||
    patchStatusMut.isPending ||
    deleteMut.isPending ||
    updatePermsMut.isPending;

  const groups = groupsQ.data ?? [];
  const permissionsCatalog = permissionsQ.data ?? [];

  const filteredGroups = useMemo(() => {
    const q = normalizeText(search);
    if (!q) return groups;

    return groups.filter((group) => {
      return (
        normalizeText(group.name).includes(q) ||
        normalizeText(group.description || "").includes(q)
      );
    });
  }, [groups, search]);

  const filteredPermissions = useMemo(() => {
    const q = normalizeText(permissionSearch);
    if (!q) return permissionsCatalog;

    return permissionsCatalog.filter((perm) => {
      return (
        normalizeText(perm.code).includes(q) ||
        normalizeText(perm.description || "").includes(q)
      );
    });
  }, [permissionsCatalog, permissionSearch]);

  useEffect(() => {
    if (!permissionsDialogOpen) return;
    if (!groupDetailQ.data) return;

    setSelectedPermissionIds(groupDetailQ.data.permission_ids || []);
  }, [permissionsDialogOpen, groupDetailQ.data]);

  function openCreateDialog() {
    if (!canWriteGroups) return;
    setEditingGroup(null);
    setGroupForm(EMPTY_FORM);
    setGroupDialogOpen(true);
  }

  function openEditDialog(group: GroupListItem) {
    if (!canWriteGroups) return;
    setEditingGroup(group);
    setGroupForm(groupToForm(group));
    setGroupDialogOpen(true);
  }

  function openPermissionsDialog(group: GroupListItem) {
    if (!canManageGroupPermissions) return;
    setPermissionsTarget(group);
    setPermissionSearch("");
    setSelectedPermissionIds([]);
    setPermissionsDialogOpen(true);
  }

  function openDeleteDialog(group: GroupListItem) {
    if (!canWriteGroups) return;
    setDeleteTarget(group);
    setDeleteDialogOpen(true);
  }

  function openStatusDialog(group: GroupListItem) {
    if (!canWriteGroups) return;
    setStatusTarget(group);
    setStatusDialogOpen(true);
  }

  function closeGroupDialog() {
    setGroupDialogOpen(false);
    setEditingGroup(null);
    setGroupForm(EMPTY_FORM);
  }

  function closePermissionsDialog() {
    setPermissionsDialogOpen(false);
    setPermissionsTarget(null);
    setSelectedPermissionIds([]);
    setPermissionSearch("");
  }

  function togglePermission(permissionId: string) {
    if (!canManageGroupPermissions) return;

    setSelectedPermissionIds((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId],
    );
  }

  function setAllFilteredPermissions(checked: boolean) {
    if (!canManageGroupPermissions) return;

    const visibleIds = filteredPermissions.map((p) => p.id);

    if (checked) {
      setSelectedPermissionIds((prev) =>
        Array.from(new Set([...prev, ...visibleIds])),
      );
      return;
    }

    setSelectedPermissionIds((prev) =>
      prev.filter((id) => !visibleIds.includes(id)),
    );
  }

  async function handleSaveGroup() {
    if (!canWriteGroups) return;
    if (!groupForm.name.trim()) {
      toast.error("Nome do grupo é obrigatório");
      return;
    }

    const body = groupToPayload(groupForm);

    try {
      if (editingGroup) {
        await updateMut.mutateAsync({ id: editingGroup.id, body });
        toast.success("Grupo atualizado");
      } else {
        await createMut.mutateAsync(body);
        toast.success("Grupo criado");
      }

      closeGroupDialog();
    } catch (e: any) {
      if (e?.message === "group_name_already_exists") {
        toast.error("Já existe um grupo com este nome.");
        return;
      }
      toast.error(e?.message || "Erro ao salvar grupo");
    }
  }

  async function handleSavePermissions() {
    if (!permissionsTarget || !canManageGroupPermissions) return;

    try {
      await updatePermsMut.mutateAsync({
        id: permissionsTarget.id,
        body: { permission_ids: selectedPermissionIds },
      });

      toast.success("Permissões atualizadas");
      closePermissionsDialog();
    } catch (e: any) {
      toast.error(e?.message || "Erro ao atualizar permissões");
    }
  }

  async function handleDeleteGroup() {
    if (!deleteTarget || !canWriteGroups) return;

    try {
      await deleteMut.mutateAsync(deleteTarget.id);
      toast.success("Grupo excluído");
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao excluir grupo");
    }
  }

  async function handleToggleStatus() {
    if (!statusTarget || !canWriteGroups) return;

    try {
      await patchStatusMut.mutateAsync({
        id: statusTarget.id,
        body: { is_active: !statusTarget.is_active },
      });

      toast.success(
        statusTarget.is_active ? "Grupo inativado" : "Grupo ativado",
      );
      setStatusDialogOpen(false);
      setStatusTarget(null);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao alterar status");
    }
  }

  const visiblePermissionIds = filteredPermissions.map((p) => p.id);
  const allFilteredSelected =
    visiblePermissionIds.length > 0 &&
    visiblePermissionIds.every((id) => selectedPermissionIds.includes(id));

  return (
    <div>
      <PageHeader
        title="Grupos"
        description="Gerenciamento de grupos de acesso e permissões"
      >
        <PermissionButton
          permission={PERMISSIONS.GROUPS_WRITE}
          onClick={openCreateDialog}
        >
          <Plus className="mr-2 h-4 w-4" />
          Novo Grupo
        </PermissionButton>
      </PageHeader>

      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar grupo por nome ou descrição..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>

        <Button
          variant="outline"
          onClick={() => setIncludeInactive((prev) => !prev)}
        >
          {includeInactive ? "Ocultar inativos" : "Mostrar inativos"}
        </Button>

        <Button
          variant="outline"
          onClick={() => groupsQ.refetch()}
          disabled={groupsQ.isFetching}
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Atualizar
        </Button>
      </div>

      {groupsQ.isLoading ? (
        <Card className="p-6 text-sm text-muted-foreground">
          Carregando grupos...
        </Card>
      ) : groupsQ.isError ? (
        <Card className="p-6">
          <div className="text-sm text-destructive">
            {(groupsQ.error as any)?.message || "Erro ao carregar grupos"}
          </div>
          <div className="mt-3">
            <Button variant="outline" onClick={() => groupsQ.refetch()}>
              Tentar novamente
            </Button>
          </div>
        </Card>
      ) : filteredGroups.length === 0 ? (
        <EmptyState title="Nenhum grupo encontrado" />
      ) : (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {filteredGroups.map((group) => (
            <Card
              key={group.id}
              className={`transition-colors hover:border-primary/30 ${
                !group.is_active ? "opacity-75" : ""
              }`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Shield className="h-4 w-4 text-primary shrink-0" />
                      <span className="truncate">{group.name}</span>
                    </CardTitle>
                  </div>

                  <CrudCardActions
                    onPermissions={() => openPermissionsDialog(group)}
                    onEdit={() => openEditDialog(group)}
                    onToggleActive={() => openStatusDialog(group)}
                    onDelete={() => openDeleteDialog(group)}
                    isActive={group.is_active}
                    disabled={isBusy}
                    permissionsPermission={[
                      PERMISSIONS.GROUPS_WRITE,
                      PERMISSIONS.PERMISSIONS_READ,
                    ]}
                    permissionsRequireAll
                    editPermission={PERMISSIONS.GROUPS_WRITE}
                    toggleActivePermission={PERMISSIONS.GROUPS_WRITE}
                    deletePermission={PERMISSIONS.GROUPS_WRITE}
                  />
                </div>
              </CardHeader>

              <CardContent>
                <div className="mb-3 flex flex-wrap gap-2">
                  <Badge variant={group.is_active ? "default" : "secondary"}>
                    {group.is_active ? "Ativo" : "Inativo"}
                  </Badge>
                </div>

                <p className="text-sm text-muted-foreground min-h-[40px]">
                  {group.description || "Sem descrição informada."}
                </p>

                <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1.5 cursor-default">
                        <Users className="h-3.5 w-3.5" />
                        <span>{group.user_count}</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Usuários ativos</p>
                    </TooltipContent>
                  </Tooltip>
                
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1.5 cursor-default">
                        <KeyRound className="h-3.5 w-3.5" />
                        <span>{group.permission_count}</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Permissões ativas</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingGroup ? "Editar Grupo" : "Novo Grupo"}
            </DialogTitle>
            <DialogDescription>
              Defina o nome, a descrição e o status do grupo.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Nome *</Label>
              <Input
                value={groupForm.name}
                onChange={(e) =>
                  setGroupForm((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="Ex.: RH"
                className="mt-1"
                disabled={!canWriteGroups}
              />
            </div>

            <div>
              <Label>Descrição</Label>
              <Input
                value={groupForm.description}
                onChange={(e) =>
                  setGroupForm((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="Descrição do grupo"
                className="mt-1"
                disabled={!canWriteGroups}
              />
            </div>

            <label className="flex items-center gap-3 rounded-lg border p-3 cursor-pointer">
              <Checkbox
                checked={groupForm.is_active}
                disabled={!canWriteGroups}
                onCheckedChange={(checked) =>
                  setGroupForm((prev) => ({
                    ...prev,
                    is_active: checked === true,
                  }))
                }
              />
              <div>
                <div className="text-sm font-medium">Grupo ativo</div>
                <div className="text-xs text-muted-foreground">
                  Grupos inativos não devem ser usados em novos vínculos.
                </div>
              </div>
            </label>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeGroupDialog}>
              Cancelar
            </Button>
            <PermissionButton
              permission={PERMISSIONS.GROUPS_WRITE}
              onClick={handleSaveGroup}
              disabled={createMut.isPending || updateMut.isPending}
            >
              {createMut.isPending || updateMut.isPending
                ? "Salvando..."
                : "Salvar"}
            </PermissionButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={permissionsDialogOpen}
        onOpenChange={setPermissionsDialogOpen}
      >
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              Permissões do Grupo {permissionsTarget ? `- ${permissionsTarget.name}` : ""}
            </DialogTitle>
            <DialogDescription>
              Selecione as permissões que devem pertencer a este grupo.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar permissão por código ou descrição..."
                  value={permissionSearch}
                  onChange={(e) => setPermissionSearch(e.target.value)}
                  className="pl-8"
                />
              </div>

              <label className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer">
                <Checkbox
                  checked={allFilteredSelected}
                  disabled={!canManageGroupPermissions}
                  onCheckedChange={(checked) =>
                    setAllFilteredPermissions(checked === true)
                  }
                />
                Selecionar visíveis
              </label>
            </div>

            <div className="rounded-lg border max-h-[420px] overflow-auto">
              {permissionsQ.isLoading || groupDetailQ.isLoading ? (
                <div className="p-4 text-sm text-muted-foreground">
                  Carregando permissões...
                </div>
              ) : permissionsQ.isError || groupDetailQ.isError ? (
                <div className="p-4 text-sm text-destructive">
                  Erro ao carregar permissões do grupo.
                </div>
              ) : filteredPermissions.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground">
                  Nenhuma permissão encontrada.
                </div>
              ) : (
                <div className="divide-y">
                  {filteredPermissions.map((permission: PermissionListItem) => {
                    const checked = selectedPermissionIds.includes(permission.id);

                    return (
                      <label
                        key={permission.id}
                        className="flex items-start gap-3 p-4 cursor-pointer hover:bg-muted/40"
                      >
                        <Checkbox
                          checked={checked}
                          disabled={!canManageGroupPermissions}
                          onCheckedChange={() => togglePermission(permission.id)}
                        />

                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-sm break-all">
                              {permission.code}
                            </span>
                            <Badge variant={permission.scope === "platform" ? "default" : "secondary"} className="text-[10px] uppercase">
                              {permission.scope === "platform" ? "Normex" : "Tenant"}
                            </Badge>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {permission.description || "Sem descrição"}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="text-xs text-muted-foreground">
              Selecionadas: {selectedPermissionIds.length}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closePermissionsDialog}>
              Cancelar
            </Button>
            <PermissionButton
              permissions={[PERMISSIONS.GROUPS_WRITE, PERMISSIONS.PERMISSIONS_READ]}
              requireAll
              onClick={handleSavePermissions}
              disabled={updatePermsMut.isPending}
            >
              {updatePermsMut.isPending ? "Salvando..." : "Salvar permissões"}
            </PermissionButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Excluir Grupo"
        description={
          deleteTarget
            ? `Tem certeza que deseja excluir o grupo "${deleteTarget.name}"?`
            : "Tem certeza que deseja excluir este grupo?"
        }
        onConfirm={handleDeleteGroup}
        confirmLabel="Excluir"
        variant="destructive"
      />

      <ConfirmDialog
        open={statusDialogOpen}
        onOpenChange={setStatusDialogOpen}
        title={statusTarget?.is_active ? "Inativar Grupo" : "Ativar Grupo"}
        description={
          statusTarget?.is_active
            ? `Deseja inativar o grupo "${statusTarget.name}"?`
            : `Deseja ativar novamente o grupo "${statusTarget?.name}"?`
        }
        onConfirm={handleToggleStatus}
        confirmLabel={statusTarget?.is_active ? "Inativar" : "Ativar"}
        variant={statusTarget?.is_active ? "destructive" : "default"}
      />
    </div>
  );
}
